import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Stack,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import type { EventGuest } from '../../types/eventGuest';
import { EVENT_GUEST_RELATIONSHIP_LABELS } from '../../types/eventGuest';

interface GuestCardProps {
  guest: EventGuest;
  onEdit: () => void;
  onDelete: () => void;
  onErase: () => void;
}

/**
 * Plan 4.8 — Card row for the MyGuests page. Three actions: edit
 * (opens GuestForm in edit mode), delete (soft-delete), and erase
 * (GDPR, opens EraseGuestDialog).
 */
const GuestCard: React.FC<GuestCardProps> = ({ guest, onEdit, onDelete, onErase }) => {
  const relationshipLabel =
    guest.relationship === 'Other' && guest.relationshipOther
      ? guest.relationshipOther
      : EVENT_GUEST_RELATIONSHIP_LABELS[guest.relationship];

  const lastUsed =
    guest.lastUsedEventTitle && guest.lastUsedEventDate
      ? `Last attended: ${guest.lastUsedEventTitle} (${guest.lastUsedEventDate})`
      : 'Not yet used';

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {guest.firstName} {guest.lastName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {relationshipLabel}
        </Typography>

        <Stack spacing={0.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2" color={guest.phone ? 'text.primary' : 'text.disabled'}>
              {guest.phone || '—'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon fontSize="small" color="action" />
            <Typography variant="body2" color={guest.email ? 'text.primary' : 'text.disabled'}>
              {guest.email || '—'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {lastUsed}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit} aria-label="Edit guest">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete} aria-label="Delete guest">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Erase permanently (GDPR)">
          <IconButton size="small" onClick={onErase} aria-label="Erase guest" color="error">
            <DeleteForeverIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default GuestCard;
